import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Phone, Check } from "lucide-react";
import { ventures } from "@/data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Enquiry.
 *
 * The previous version was the same form every website has: name, email, a
 * dropdown, a subject, a message, send. Six completely different buyer
 * journeys were buried inside one <select>, which is the opposite of
 * considered, and every one of them was asked the same four questions.
 *
 * Three decisions here:
 *
 *  1. The path is the first visible choice, as real targets rather than a
 *     dropdown. It is the only question everyone answers, and it decides what
 *     is asked next.
 *
 *  2. Each path asks only what it needs, so nobody fills in four fields that do
 *     not apply. A build enquiry needs a place and a timeline. A press request
 *     needs an outlet and a date. They are not the same form.
 *
 *  3. A stay or a table does NOT open a form. Sending a table request to an
 *     architect's inbox is slower for the guest and useless to her, so that
 *     path hands over the venue's own booking surface and phone number. The
 *     best enquiry form is sometimes not a form.
 */

const PATHS = [
  {
    id: "project",
    label: "A project",
    hint: "A home, a restoration, interiors, or a venue",
  },
  {
    id: "stay",
    label: "A stay or a table",
    hint: "Mae Terra, Earth Alive, The Balcony Bar",
  },
  {
    id: "press-speaking",
    label: "Press or speaking",
    hint: "An article, a panel, a stage",
  },
];

const PROJECT_KINDS = [
  { id: "residence", label: "A home" },
  { id: "restoration", label: "A restoration" },
  { id: "interiors", label: "Interiors only" },
  { id: "hospitality", label: "A venue" },
];

const TIMELINES = [
  { id: "exploring", label: "Still exploring" },
  { id: "3-months", label: "Within 3 months" },
  { id: "6-months", label: "Within 6 months" },
  { id: "underway", label: "Already underway" },
];

const NEXT_STEPS = [
  "It reaches Shruthi directly, not an assistant.",
  "She replies within two working days, including a no.",
  "If it fits, a call and a first sketch of scope.",
];

/** A real, tappable choice. Radio semantics, so it is keyboard operable. */
const Choice = ({ name, value, checked, onChange, label, hint, testid }) => (
  <label
    data-testid={testid}
    data-cursor="hover"
    className={`group relative flex cursor-pointer flex-col justify-between gap-2 border px-5 py-4 transition-colors duration-300 ${
      checked
        ? "border-ink bg-ink text-cream"
        : "border-ink/15 text-ink hover:border-ink hover:bg-ink/[0.04]"
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    <span className="flex items-start justify-between gap-3">
      <span className="font-display text-display-s leading-tight">{label}</span>
      <span
        aria-hidden="true"
        className={`mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors ${
          checked ? "border-cream bg-cream" : "border-ink/40"
        }`}
      >
        {checked && <Check size={11} className="text-ink" strokeWidth={3} />}
      </span>
    </span>
    {hint && (
      <span
        className={`font-sans text-[13px] leading-snug ${
          checked ? "text-cream/70" : "text-ink2"
        }`}
      >
        {hint}
      </span>
    )}
  </label>
);

/** Small pill for the secondary choices. */
const Pill = ({ name, value, checked, onChange, label, testid }) => (
  <label
    data-testid={testid}
    data-cursor="hover"
    className={`cursor-pointer border px-4 py-2.5 label transition-colors duration-300 ${
      checked
        ? "border-terracotta bg-terracotta text-cream"
        : "border-ink/15 text-ink2 hover:border-ink hover:text-ink"
    }`}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    {label}
  </label>
);

const Field = ({ id, label, error, children, className = "" }) => (
  <div className={className}>
    <label className="chapter-num mb-2 block" htmlFor={id}>
      {label}
    </label>
    {children}
    {error && (
      <p
        className="mt-2 label text-terracotta2"
        data-testid={`error-${id}`}
      >
        {error}
      </p>
    )}
  </div>
);

export const Contact = () => {
  const [path, setPath] = useState("project");
  const [form, setForm] = useState({
    name: "",
    email: "",
    kind: "residence",
    place: "",
    timeline: "exploring",
    outlet: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [fallback, setFallback] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((x) => (x[k] ? { ...x, [k]: null } : x));
  };

  const projectType = path === "project" ? form.kind : path;

  /** Compose one readable block, because this is what lands in her inbox. */
  const composed = () => {
    const lines = [];
    if (path === "project") {
      lines.push(`Kind: ${PROJECT_KINDS.find((k) => k.id === form.kind)?.label}`);
      if (form.place) lines.push(`Where: ${form.place}`);
      lines.push(
        `Timeline: ${TIMELINES.find((t) => t.id === form.timeline)?.label}`
      );
    }
    if (path === "press-speaking" && form.outlet) {
      lines.push(`Outlet or event: ${form.outlet}`);
    }
    return `${lines.join("\n")}\n\n${form.message}`.trim();
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "That address looks incomplete";
    if (!form.message.trim()) e.message = "A line or two is enough";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const mailtoFallback = () =>
    `mailto:shruthiravioli@gmail.com?subject=${encodeURIComponent(
      `${projectType} enquiry from ${form.name}`
    )}&body=${encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${composed()}\n`
    )}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("A couple of fields still need you.");
      return;
    }
    setSending(true);
    setFallback(null);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        project_type: projectType,
        subject: form.place || form.outlet || undefined,
        message: composed(),
      });
      if (data && data.delivered === false) {
        setFallback(mailtoFallback());
        toast.error("The form could not reach the studio. Send it by email.");
        return;
      }
      setDone(true);
    } catch (err) {
      console.error(err);
      setFallback(mailtoFallback());
      toast.error("The form could not reach the studio. Send it by email.");
    } finally {
      setSending(false);
    }
  };

  const bookable = ventures.filter((v) => v.bookable);

  return (
    <section
      id="contact"
      className="relative bg-cream text-ink py-24 md:py-32 border-t border-ink/15"
      data-testid="contact-section"
    >
      <Toaster position="top-center" richColors />
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="grid grid-cols-12 items-end gap-y-6 gap-x-6 md:gap-x-10 mb-14 md:mb-16"
        >
          <div className="col-span-12 md:col-span-7">
            <p className="chapter-num">Enquiries</p>
            <h2 className="mt-5 font-display text-display-l text-ink max-w-[20ch]">
              Tell me what you need.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 font-sans font-light text-base md:text-lg text-ink2 leading-relaxed md:text-right">
            Three questions, then only what your kind of project actually needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-12 gap-y-14 gap-x-6 md:gap-x-14">
          {/* Left: expectations and the direct line */}
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-[112px]">
              <p className="chapter-num">What happens next</p>
              <ol className="mt-5 space-y-4">
                {NEXT_STEPS.map((s, i) => (
                  <li key={s} className="flex gap-4 border-t border-ink/15 pt-4">
                    <span className="label text-terracotta2 pt-1">
                      0{i + 1}
                    </span>
                    <span className="font-sans font-light text-[15px] text-ink2 leading-snug">
                      {s}
                    </span>
                  </li>
                ))}
              </ol>

              <div className="mt-10 border-t border-ink pt-5">
                <p className="chapter-num">Or write directly</p>
                <div className="mt-3 flex flex-col items-start gap-1.5">
                  <a
                    href="mailto:shruthiravioli@gmail.com"
                    className="editorial-link font-sans text-base text-ink"
                    data-testid="contact-email-link"
                    data-cursor="hover"
                  >
                    shruthiravioli@gmail.com
                  </a>
                  <a
                    href="tel:+13128682030"
                    className="editorial-link font-sans text-base text-ink"
                    data-testid="contact-phone-link"
                    data-cursor="hover"
                  >
                    +1 312 868 2030
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: the intake */}
          <div className="col-span-12 lg:col-span-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                className="border border-ink bg-ink text-cream px-8 py-14 md:px-14 md:py-20"
                data-testid="contact-success"
              >
                <p className="label text-gold">
                  Received
                </p>
                <p className="mt-6 font-display text-display-m max-w-[24ch]">
                  Thank you. It is with Shruthi now.
                </p>
                <p className="mt-5 font-sans font-light text-base md:text-lg text-cream/70 max-w-[46ch] leading-relaxed">
                  You will hear back within two working days. If it is urgent,
                  the phone is faster than the form.
                </p>
              </motion.div>
            ) : (
              <>
                {/* Step one, always visible */}
                <fieldset>
                  <legend className="chapter-num mb-4">
                    What brings you here
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {PATHS.map((p) => (
                      <Choice
                        key={p.id}
                        name="path"
                        value={p.id}
                        checked={path === p.id}
                        onChange={() => {
                          setPath(p.id);
                          setErrors({});
                        }}
                        label={p.label}
                        hint={p.hint}
                        testid={`path-${p.id}`}
                      />
                    ))}
                  </div>
                </fieldset>

                {/* No AnimatePresence and no mode="wait" here. Gating the new
                    branch behind the old one's exit put 450ms of dead air
                    between the click and any response, and it never resolved at
                    all when requestAnimationFrame was throttled. The branch now
                    mounts immediately and animates in on its own. */}
                <div>
                  {/* A stay is not a form. Hand over the real booking surface. */}
                  {path === "stay" ? (
                    <motion.div
                      key="stay"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                      className="mt-10"
                      data-testid="stay-handoff"
                    >
                      <p className="font-sans font-light text-base md:text-lg text-ink2 max-w-[52ch] leading-relaxed">
                        Bookings are faster straight with the place. Each one
                        takes its own dates and answers the same day.
                      </p>
                      <ul className="mt-7">
                        {bookable.map((v) => (
                          <li
                            key={v.slug}
                            className="flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 py-5 last:border-b"
                            data-testid={`stay-option-${v.slug}`}
                          >
                            <div>
                              <p className="font-display text-display-s text-ink">
                                {v.title}
                              </p>
                              <p className="mt-1 label text-ink2">
                                {v.location}
                                {v.rates.length > 0 &&
                                  ` · from ${v.rates
                                    .map((r) => r.price)
                                    .sort(
                                      (a, b) =>
                                        Number(String(a).replace(/\D/g, "")) -
                                        Number(String(b).replace(/\D/g, ""))
                                    )[0]}`}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                              {v.contact.phone && (
                                <a
                                  href={`tel:${v.contact.phone}`}
                                  data-cursor="hover"
                                  data-testid={`stay-call-${v.slug}`}
                                  className="inline-flex items-center gap-2 border border-ink/40 px-4 py-2.5 label text-ink hover:bg-ink hover:text-cream transition-colors duration-300"
                                >
                                  <Phone size={12} />
                                  Call
                                </a>
                              )}
                              <a
                                href={`${v.booking.href}${
                                  v.booking.href.includes("?") ? "&" : "?"
                                }utm_source=shruthiravi&utm_medium=portfolio`}
                                target="_blank"
                                rel="noopener"
                                data-cursor="hover"
                                data-testid={`stay-book-${v.slug}`}
                                className="group inline-flex items-center gap-2 bg-ink px-4 py-2.5 label text-cream hover:bg-terracotta transition-colors duration-300"
                              >
                                {v.booking.label}
                                <ArrowUpRight
                                  size={12}
                                  className="transition-transform duration-300 group-hover:rotate-45"
                                />
                              </a>
                            </div>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 font-sans font-light text-[15px] text-ink2">
                        Planning something larger, like a takeover or an event?{" "}
                        <button
                          type="button"
                          onClick={() => setPath("project")}
                          data-cursor="hover"
                          data-testid="stay-to-project"
                          className="editorial-link text-ink"
                        >
                          Write to me instead.
                        </button>
                      </p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key={path}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                      onSubmit={onSubmit}
                      className="mt-10 space-y-8"
                      data-testid="contact-form"
                      noValidate
                    >
                      {path === "project" && (
                        <fieldset>
                          <legend className="chapter-num mb-3">
                            Which of these
                          </legend>
                          <div className="flex flex-wrap gap-2.5">
                            {PROJECT_KINDS.map((k) => (
                              <Pill
                                key={k.id}
                                name="kind"
                                value={k.id}
                                checked={form.kind === k.id}
                                onChange={set("kind")}
                                label={k.label}
                                testid={`kind-${k.id}`}
                              />
                            ))}
                          </div>
                        </fieldset>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        <Field id="name" label="Your name" error={errors.name}>
                          <input
                            id="name"
                            data-testid="contact-name"
                            className="editorial-input"
                            value={form.name}
                            onChange={set("name")}
                            autoComplete="name"
                          />
                        </Field>
                        <Field id="email" label="Email" error={errors.email}>
                          <input
                            id="email"
                            type="email"
                            data-testid="contact-email"
                            className="editorial-input"
                            value={form.email}
                            onChange={set("email")}
                            autoComplete="email"
                          />
                        </Field>

                        {path === "project" ? (
                          <Field id="place" label="Where is it">
                            <input
                              id="place"
                              data-testid="contact-place"
                              className="editorial-input"
                              placeholder="City, or the plot if you have one"
                              value={form.place}
                              onChange={set("place")}
                            />
                          </Field>
                        ) : (
                          <Field id="outlet" label="Publication or event">
                            <input
                              id="outlet"
                              data-testid="contact-outlet"
                              className="editorial-input"
                              value={form.outlet}
                              onChange={set("outlet")}
                            />
                          </Field>
                        )}

                        {path === "project" && (
                          <div>
                            <p className="chapter-num mb-2">When</p>
                            <div className="flex flex-wrap gap-2">
                              {TIMELINES.map((t) => (
                                <Pill
                                  key={t.id}
                                  name="timeline"
                                  value={t.id}
                                  checked={form.timeline === t.id}
                                  onChange={set("timeline")}
                                  label={t.label}
                                  testid={`timeline-${t.id}`}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <Field
                        id="message"
                        label={
                          path === "project"
                            ? "Tell me about the space"
                            : "Tell me more"
                        }
                        error={errors.message}
                      >
                        <textarea
                          id="message"
                          data-testid="contact-message"
                          className="editorial-input min-h-[140px] resize-y"
                          placeholder={
                            path === "project"
                              ? "What is there now, and what you want it to do."
                              : "The angle, the date, and what you need from me."
                          }
                          value={form.message}
                          onChange={set("message")}
                        />
                      </Field>

                      {fallback && (
                        <a
                          href={fallback}
                          data-testid="contact-mailto-fallback"
                          data-cursor="hover"
                          className="flex items-center justify-between gap-4 border border-terracotta bg-terracotta/5 px-5 py-4 label text-terracotta hover:bg-terracotta hover:text-cream transition-colors duration-500"
                        >
                          Send it by email instead
                          <ArrowUpRight size={14} />
                        </a>
                      )}

                      <div className="flex flex-col-reverse sm:flex-row-reverse sm:items-center sm:justify-between gap-5 border-t border-ink pt-7">
                        <p className="label text-ink2 max-w-[34ch]">
                          No newsletter, no CRM, no follow up sequence.
                        </p>
                        <button
                          type="submit"
                          disabled={sending}
                          data-testid="contact-submit"
                          data-cursor="hover"
                          className="group inline-flex items-center justify-center gap-3 bg-ink px-8 py-4 label text-cream transition-colors duration-500 hover:bg-terracotta disabled:opacity-60"
                        >
                          {sending ? "Sending" : "Send it"}
                          <ArrowUpRight
                            size={15}
                            className="transition-transform duration-300 group-hover:rotate-45"
                          />
                        </button>
                      </div>
                    </motion.form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
